import Text from "@/components/ui/text";
import { Commercials, DealData, DealStatus } from "@/pages/deals/types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useDealCommercialsMutation } from "@/pages/deals/hooks";
import {
  useContract,
  useUpdateContract,
  type ContractUpdatePayload
} from "@/pages/contracts/hooks";
import { ContractData } from "@/pages/contracts/types";
import ContractFinancialRecognition from "@/pages/contracts/ContractDetailedView/financialRecognition/ContractFinancialRecognition";
import { type ContractFieldUpdate } from "@/pages/contracts/ContractDetailedView/ContractDetailedView";
import ContractTermsV2 from "./ContractTermsV2/ContractTermsV2";
import Files from "./Files/Files";
import { ProposalsSection } from "@/pages/contracts/components/proposals/ProposalsSection";
import {
  FINANCIALS_WRITE_PERMISSION,
  useHasPermission
} from "@/services/auth/AuthContext";

interface Props {
  dealData: DealData;
  onDealUpdate?: (updatedFields: Partial<DealData>) => void;
}

const DealCommercialsV2: React.FC<Props> = ({ dealData, onDealUpdate }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [commercials, setCommercials] = useState<Commercials | null>(null);
  const getOrUpdateCommercials = useDealCommercialsMutation();
  const { data: contract } = useContract(dealData.contractId ?? undefined);
  const updateContract = useUpdateContract(dealData.contractId ?? undefined);
  const canEditFinancials = useHasPermission(FINANCIALS_WRITE_PERMISSION);
  // Mutation objects get new identities on every state change — keep them in
  // refs so the update callbacks stay referentially stable.
  const updateContractRef = useRef(updateContract);
  const getOrUpdateCommercialsRef = useRef(getOrUpdateCommercials);
  useEffect(() => {
    updateContractRef.current = updateContract;
    getOrUpdateCommercialsRef.current = getOrUpdateCommercials;
  }, [updateContract, getOrUpdateCommercials]);
  // Financial fields lock down once the deal is closed won; open deals stay
  // editable by everyone.
  const financialsLocked =
    dealData.status === DealStatus.CLOSED_WON && !canEditFinancials;

  useEffect(() => {
    if (!dealData?.id) return;
    getOrUpdateCommercials.mutate(
      { pathParams: { dealId: dealData.id }, data: {} },
      { onSuccess: setCommercials }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealData?.id]);

  const onUpdateContractField = useCallback<ContractFieldUpdate>(
    (field, newValue, additionalFields) => {
      if (financialsLocked) return;
      if (!dealData.contractId) return;
      // CARR/CMRR manual overrides: 0 is equivalent to "no override"
      const nullableMonetaryFields = ["carrLocal", "cmrrLocal"];
      const finalValue =
        nullableMonetaryFields.includes(field) && newValue === 0
          ? null
          : newValue;
      const changedFields: Partial<ContractUpdatePayload> = {
        dealId: dealData.id,
        [field]: finalValue,
        ...(additionalFields || {})
      };
      queryClient.setQueryData(
        [
          "contracts/id",
          { pathParams: { contractId: dealData.contractId } },
          undefined
        ],
        (old: ContractData | undefined) =>
          old ? { ...old, ...changedFields } : old
      );
      updateContractRef.current.mutate(
        {
          pathParams: { contractId: dealData.contractId },
          data: changedFields
        },
        {
          onSuccess: () =>
            queryClient.invalidateQueries({
              queryKey: ["deals", { pathParams: { dealsIds: dealData.id } }]
            }),
          onError: () => toast.error(t("deals.commercials.updateError"))
        }
      );
    },
    [dealData.contractId, dealData.id, financialsLocked, queryClient, t]
  );

  const onUpdateCommercials = useCallback(
    (
      field: string,
      newValue: unknown,
      additionalFields?: { [fieldName: string]: string | number | null }
    ) => {
      if (financialsLocked) return;
      if (!commercials) return;
      const changedFields = { [field]: newValue, ...(additionalFields || {}) };
      setCommercials({ ...commercials, ...changedFields });
      getOrUpdateCommercialsRef.current.mutate({
        pathParams: { dealId: dealData.id },
        data: changedFields
      });
    },
    [commercials, dealData.id, financialsLocked]
  );

  return (
    <div className="flex flex-col lg-mid:flex-row w-full max-w-[1800px] mx-auto gap-10">
      <div className="flex-1 min-w-0 flex flex-col gap-4">
        <Text variant="lgSubtitleH1">
          {t("deals.commercials.contractTermsTitle")}
        </Text>
        <div>
          <ContractTermsV2
            contractId={dealData.contractId ?? undefined}
            productId={dealData.productId ?? undefined}
            dealId={dealData.id}
            companyId={dealData.companyId ?? undefined}
            onDealUpdate={onDealUpdate}
            showCommittedValues={false}
            showStatusColumn={false}
            financialsLocked={financialsLocked}
            offerBundles
          />
        </div>
        <ProposalsSection
          className="mt-6"
          dealId={dealData.id}
          contractId={dealData.contractId ?? undefined}
        />
      </div>
      <div className="w-full flex flex-row lg-mid:flex-col lg-mid:w-[clamp(280px,calc(31.25vw_-_220px),380px)] lg-mid:shrink-0 gap-10">
        <div className="flex-1 lg-mid:flex-none flex flex-col gap-6 min-w-0">
          {contract ? (
            <ContractFinancialRecognition
              contract={contract}
              onUpdateField={onUpdateContractField}
              editDisabled={financialsLocked}
              editDisabledTooltipText={t("common.noEditPermission")}
              showCommittedValues={false}
            />
          ) : null}
        </div>
        <div className="flex-1 lg-mid:flex-none flex flex-col gap-4 min-w-0">
          <Text variant="lgSubtitleH1">
            {t("deals.commercials.filesTitle")}
          </Text>
          <Files
            attachments={commercials?.uploadContracts?.contracts ?? []}
            onAttachmentsChange={(attachments) =>
              onUpdateCommercials("uploadContracts", { contracts: attachments })
            }
            disabled={financialsLocked}
            disabledTooltipText={t("common.noEditPermission")}
          />
        </div>
      </div>
    </div>
  );
};

export default DealCommercialsV2;
